// @flow
import { connectManateeSocket } from './ManateeSocket'
import { Auth } from 'services'
import Event from './Event'

/**
 * Socket for running the Manatee debugger.
 * @function
 * @namespace DebugSocket
 * @version 1.0.0
 * @description
 * The debug-socket is used for communicating directly with a Manatee running on the same machine as Cuesta.
 * @return { Object } Exposes `on`, `off` methods to subscribe to the events described above and `remove` to close the socket connection as well.
 */

/**
 * Represents a debug-socket.
 * @typedef { Object } DebugSocket
 * @memberof DebugSocket
 */
type DebugSocket = {
  on(name: string, method: Function): void,
  off(name: string, method: Function): void,
  runFlow(flow: Object, inputs: Array<string>, delay: ?Number): void,
  debugFlow(flow: Object, inputs: Array<string>, delay: ?Number): void,
  pauseFlow(): void,
  forwardFlow(): void,
  stopFlow(): void,
  remove(): void
}

function handleSocketMessages(ws, event) {
  ws.onmessage = msg => {
    try {
      const data = JSON.parse(msg.data)
      event.emit('message', data)
      if (data.Action && data.Action.toLowerCase() === 'finished')
        event.emit('finished', {
          decision: data.Decision,
          result: data.Result,
          reason: data.Reason
        })
      else if (data.Action && data.Action.toLowerCase() === 'step')
        event.emit('step', { state: data.State, step: data.Step })
      else if (data.Action && data.Action.toLowerCase() === 'state')
        event.emit('state', { state: data.State })
    } catch (e) {
      console.warn('Received unintelligible debug socket message: ', msg)
    }
  }
}

export default (conf: { host: ?string, port: ?number, securePort: ?number }): DebugSocket => {
  const event = new Event()
  let ws

  let closed = false
  connectManateeSocket(
    'flowtracer',
    w => (ws = w),
    event,
    conf.host,
    conf.port,
    conf.securePort,
    () => closed
  )

  event.on('open', () => handleSocketMessages(ws, event))

  const remove = () => ((closed = true), ws.close(), event.reset())

  const runFlow = (
    flow: Object,
    inputs: Array<Object>,
    delay: ?Number,
    noTracerContext: ?boolean
  ) => {
    ws.send(
      JSON.stringify({
        Authentication: Auth.getCredentials(),
        Action: 'run',
        Flow: {
          Identifier: flow.id,
          Name: flow.name,
          Descriptive: { ...flow, precompile: false }
        },
        Inputs: inputs.reduce((map, obj) => {
          return { ...map, [obj.key]: obj.value }
        }, {}),
        DelayMs: delay,
        NoTracerContext: noTracerContext
      })
    )
  }

  const debugFlow = (flow: Object, inputs: Array<Object>, delay: ?Number) => {
    ws.send(
      JSON.stringify({
        Authentication: Auth.getCredentials(),
        Action: 'debug',
        Flow: {
          Identifier: flow.id,
          Name: flow.name,
          Descriptive: { ...flow, precompile: false }
        },
        Inputs: inputs.reduce((map, obj) => {
          return { ...map, [obj.key]: obj.value }
        }, {}),
        DelayMs: delay
      })
    )
  }

  const stopFlow = () => {
    ws.send(
      JSON.stringify({
        Authentication: Auth.getCredentials(),
        Action: 'stop',
        Flow: null
      })
    )
  }

  const pauseFlow = () => {
    ws.send(
      JSON.stringify({
        Authentication: Auth.getCredentials(),
        Action: 'pause',
        Flow: null
      })
    )
  }

  const forwardFlow = () => {
    ws.send(
      JSON.stringify({
        Authentication: Auth.getCredentials(),
        Action: 'forward',
        Flow: null
      })
    )
  }

  return {
    on: event.on,
    off: event.off,
    remove,
    runFlow,
    debugFlow,
    pauseFlow,
    stopFlow,
    forwardFlow
  }
}
